import { useState, useEffect } from 'react';

import { usePathParam } from 'react-resource-router';

import { compassApi } from 'src/sections/workspace/sections/settings/components/compass/compass-requests';

type ReturnValue = {
  isLoading: boolean;
  cloudId: string | undefined;
  error: Error | null;
};

/**
 * Fetches the connected Compass site's Cloud ID for current workspace.
 * @param workspacePathParam The router path param that contains the workspace name.
 */
export const useCompassCloudIdForWorkspace = (
  workspacePathParam: string
): ReturnValue => {
  const [isLoading, setLoading] = useState(false);
  const [cloudId, setCloudId] = useState<string>();
  const [error, setError] = useState<Error | null>(null);
  const [owner] = usePathParam(workspacePathParam);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!owner) {
          setLoading(false);
          setError(new Error('Invalid workspace slug'));
          return;
        }

        const api = compassApi();
        const result = await api.getCloudIdsForWorkspace(owner);

        const connectedCloudId = result?.connectedCloudIds?.[0];

        setCloudId(connectedCloudId);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        if ('status' in e && e.status !== 404) {
          setError(e);
        }
      }
    };
    fetchData();
  }, [owner]);

  return {
    isLoading,
    cloudId,
    error,
  };
};

/**
 * Fetches the connected Compass site's Cloud ID for current repository.
 * @param workspacePathParam The router path param that contains the workspace name.
 * @param repositoryPathParam The router path param that contains the repository name.
 */

const api = compassApi();

export const useCompassCloudIdForRepository = (
  workspacePathParam: string,
  repositoryPathParam: string
): ReturnValue => {
  const [isLoading, setLoading] = useState(false);
  const [cloudId, setCloudId] = useState<string>();
  const [error, setError] = useState<Error | null>(null);
  const [owner] = usePathParam(workspacePathParam);
  const [repository] = usePathParam(repositoryPathParam);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!owner) {
          setLoading(false);
          setError(new Error('Invalid workspace slug'));
          return;
        }

        if (!repository) {
          setLoading(false);
          setError(new Error('Invalid repository slug'));
          return;
        }

        const result = await api.getCloudIdsForRepository(owner, repository);

        const connectedCloudId = result?.connectedCloudIds?.[0];

        setCloudId(connectedCloudId);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        if ('status' in e && e.status !== 404) {
          setError(e);
        }
      }
    };
    fetchData();
  }, [owner, repository]);

  return {
    isLoading,
    cloudId,
    error,
  };
};

export { useCompassSiteInfo } from 'src/sections/workspace/sections/settings/components/compass/use-compass-site-info';
